import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { StatusBadge, fmtDate } from "@/components/common";
import InlineEdit from "@/components/InlineEdit";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { updateResource } from "@/lib/api";

const STATUSES = ["pickup_scheduled", "delivery_scheduled", "picked_up", "delivered"];

// Shows the sale status with the scheduled pickup/delivery date underneath it,
// mirroring the Facilities "Contacted" cell. Both are click-to-edit.
export default function SaleStatusCell({ row }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [delivOpen, setDelivOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");

  const setStatus = async (val) => {
    if (val === "delivery_scheduled") {
      setPhone(row.customer_phone || "");
      setAddress(row.address || "");
      setOpen(false);
      setDelivOpen(true);
      return;
    }
    try {
      await updateResource("sales", row.id, { status: val });
      qc.invalidateQueries({ queryKey: ["sales"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      toast.success("Status updated");
      setOpen(false);
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const confirmDelivery = async () => {
    if (!address.trim()) { toast.error("Enter a delivery address"); return; }
    try {
      await updateResource("sales", row.id, { status: "delivery_scheduled", customer_phone: phone.trim(), address: address.trim() });
      qc.invalidateQueries({ queryKey: ["sales"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      qc.invalidateQueries({ queryKey: ["trucking"] });
      toast.success("Delivery scheduled — sent to Trucking");
      setDelivOpen(false);
    } catch {
      toast.error("Couldn't schedule delivery");
    }
  };

  const toggleWebsite = async () => {
    try {
      await updateResource("sales", row.id, { off_website: !row.off_website });
      qc.invalidateQueries({ queryKey: ["sales"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
    } catch {
      toast.error("Couldn't update");
    }
  };

  const schedLabel = (r) => {
    if (!r.scheduled_date) return <span className="text-gray-400 italic text-sm">add date</span>;
    const prefix = r.status === "delivery_scheduled" || r.status === "delivered" ? "Delivery"
      : r.status === "pickup_scheduled" || r.status === "picked_up" ? "Pickup" : "";
    return <span className="text-sm text-gray-700">{prefix ? `${prefix} · ` : ""}{fmtDate(r.scheduled_date)}</span>;
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid="sale-status-trigger" className="focus:outline-none">
            <StatusBadge status={row.status} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="rounded-none border-2 border-gray-900 w-52 p-2">
          <div className="flex flex-col gap-1">
            {STATUSES.map((s) => (
              <button key={s} data-testid={`sale-status-opt-${s}`} onClick={() => setStatus(s)}
                className="text-left px-1 py-1 hover:bg-gray-100">
                <StatusBadge status={s} />
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <InlineEdit row={row} path="sales" field="scheduled_date" type="date" label="Pickup / Delivery Date" render={schedLabel} />
      <button data-testid="sale-offweb-toggle" onClick={toggleWebsite}
        title={row.off_website ? "Off the website" : "Still on the website — click to mark off"}
        className="flex items-center gap-1 text-xs font-medium mt-0.5 focus:outline-none">
        {row.off_website
          ? <><Check className="w-3.5 h-3.5 text-green-600" /><span className="text-green-700">Off website</span></>
          : <><X className="w-3.5 h-3.5 text-gray-400" /><span className="text-gray-400">On website</span></>}
      </button>

      <Dialog open={delivOpen} onOpenChange={setDelivOpen}>
        <DialogContent className="rounded-none border-2 border-gray-900">
          <DialogHeader>
            <DialogTitle className="font-head uppercase">Schedule Delivery</DialogTitle>
            <DialogDescription>Add the customer's phone & address — this creates a delivery stop in Trucking.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-gray-500">Customer Phone</label>
              <Input data-testid="sale-delivery-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="712-555-0143" className="mt-1 rounded-none border-2 border-gray-900" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-gray-500">Delivery Address <span className="text-[#D50000]">*</span></label>
              <Input data-testid="sale-delivery-address" value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="112 S 16th St, Clarinda, IA" className="mt-1 rounded-none border-2 border-gray-900" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelivOpen(false)} className="rounded-none border-2">Cancel</Button>
            <Button data-testid="sale-delivery-confirm" onClick={confirmDelivery} className="rounded-none bg-[#0D9488] hover:bg-[#0b7a70]">Schedule & Send to Trucking</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
